import React from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import AnimatedSection from "../components/common/AnimatedSection";
import SectionHeading from "../components/common/SectionHeading";

// Placeholder blog posts (in production, these would come from markdown files)
const blogPosts = [
  {
    slug: "choosing-right-materials-kitchen",
    title: "Choosing the Right Materials for Your Kitchen",
    excerpt:
      "Learn why we use MR Ply, BWR Ply, BWP Ply, and HDHMR instead of MDF, and how to select the perfect material for your kitchen based on usage and moisture exposure.",
    date: "2024-12-01",
    readTime: "5 min",
    category: "Materials",
    image:
      "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&q=80",
    featured: true,
  },
  {
    slug: "modular-vs-carpentry-comparison",
    title: "Modular vs Carpentry: A Detailed Comparison",
    excerpt:
      "Factory-fit modular interiors vs traditional carpentry – understand the differences in quality, timeline, cost, and longevity.",
    date: "2024-11-15",
    readTime: "7 min",
    category: "Guide",
    image:
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80",
    featured: true,
  },
  {
    slug: "wardrobe-storage-solutions",
    title: "10 Wardrobe Storage Solutions for Indian Homes",
    excerpt:
      "Maximize your wardrobe space with these smart storage ideas – from saree organizers to shoe racks, designed for how Indians actually live.",
    date: "2024-11-01",
    readTime: "6 min",
    category: "Design Tips",
    image:
      "https://images.unsplash.com/photo-1558997519-83ea9252edf8?w=800&q=80",
    featured: false,
  },
  {
    slug: "kitchen-layout-guide",
    title: "Kitchen Layout Guide: L-Shaped vs U-Shaped vs Island",
    excerpt:
      "Which kitchen layout works best for your space? A comprehensive guide to choosing between L-shaped, U-shaped, parallel, and island kitchens.",
    date: "2024-10-20",
    readTime: "8 min",
    category: "Kitchen",
    image:
      "https://images.unsplash.com/photo-1600489000022-c2086d79f9d4?w=800&q=80",
    featured: false,
  },
  {
    slug: "rental-interior-packages",
    title: "Smart Interior Packages for Rental Properties",
    excerpt:
      "How to get quality interiors for your investment property without breaking the bank. Our rental packages starting at \u20b93 Lakhs.",
    date: "2024-10-05",
    readTime: "4 min",
    category: "Budget",
    image:
      "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=800&q=80",
    featured: false,
  },
  {
    slug: "pooja-room-design-ideas",
    title: "Modern Pooja Room Design Ideas",
    excerpt:
      "Sacred spaces that blend tradition with contemporary design. Explore pooja room ideas from compact units to dedicated rooms.",
    date: "2024-09-25",
    readTime: "5 min",
    category: "Design Tips",
    image:
      "https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=800&q=80",
    featured: false,
  },
];

const categories = [
  "All",
  "Materials",
  "Guide",
  "Design Tips",
  "Kitchen",
  "Budget",
];

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = React.useState("All");

  const filteredPosts =
    activeCategory === "All"
      ? blogPosts
      : blogPosts.filter((post) => post.category === activeCategory);

  const featuredPosts = blogPosts.filter((post) => post.featured);

  return (
    <div>
      {/* Hero */}
      <section className="py-32 lg:py-40 bg-gradient-brand">
        <div className="container-custom section-padding">
          <AnimatedSection className="max-w-3xl text-white">
            <span className="text-brand-accent text-sm font-medium uppercase tracking-wider">
              Our Blog
            </span>
            <h1 className="font-heading text-display-1 mt-4 mb-6 text-white">
              Interior insights & inspiration.
            </h1>
            <p className="text-xl text-white/80 leading-relaxed">
              Design tips, material guides, and ideas to help you make informed
              decisions for your home interiors.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Featured Posts */}
      <section className="section-spacing bg-white">
        <div className="container-custom section-padding">
          <SectionHeading
            label="Featured"
            title="Editor's picks."
            align="left"
          />

          <div className="grid md:grid-cols-2 gap-8">
            {featuredPosts.map((post, index) => (
              <AnimatedSection key={post.slug} delay={index * 100}>
                <Link to={`/blog/${post.slug}`} className="group block">
                  <div className="relative aspect-[16/10] rounded-2xl overflow-hidden mb-6">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-brand-accent rounded-full text-xs font-medium text-white">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-text-secondary mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(post.date).toLocaleDateString("en-IN", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {post.readTime} read
                    </span>
                  </div>
                  <h3 className="font-heading text-2xl text-brand-primary group-hover:text-brand-accent transition-colors mb-3">
                    {post.title}
                  </h3>
                  <p className="text-text-secondary leading-relaxed">
                    {post.excerpt}
                  </p>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* All Posts */}
      <section className="section-spacing bg-background-main">
        <div className="container-custom section-padding">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <SectionHeading
              label="All Articles"
              title="Browse by category."
              align="left"
              className="mb-0"
            />
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeCategory === cat
                      ? "bg-brand-accent text-white"
                      : "bg-white text-text-secondary hover:text-brand-primary shadow-soft"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post, index) => (
              <AnimatedSection key={post.slug} delay={index * 50}>
                <Link
                  to={`/blog/${post.slug}`}
                  className="group block bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-medium transition-shadow"
                >
                  <div className="aspect-[16/10]">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-text-secondary mb-3">
                      <span className="px-2 py-0.5 bg-brand-accent/10 rounded text-brand-accent text-xs font-medium">
                        {post.category}
                      </span>
                      <span>{post.readTime} read</span>
                    </div>
                    <h3 className="font-heading text-xl text-brand-primary group-hover:text-brand-accent transition-colors mb-2">
                      {post.title}
                    </h3>
                    <p className="text-text-secondary text-sm line-clamp-2">
                      {post.excerpt}
                    </p>
                  </div>
                </Link>
              </AnimatedSection>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-20">
              <p className="text-text-secondary">
                No articles in this category yet.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 bg-gradient-brand">
        <div className="container-custom section-padding text-center">
          <AnimatedSection>
            <h2 className="font-heading text-h2 text-white mb-4">
              Get interior tips in your inbox.
            </h2>
            <p className="text-white/80 max-w-xl mx-auto mb-8">
              Subscribe to our newsletter for design inspiration, material
              guides, and exclusive offers.
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-5 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:border-white/50"
              />
              <button
                type="submit"
                className="px-6 py-3 bg-white text-brand-primary rounded-full font-medium hover:bg-white/90 transition-colors"
              >
                Subscribe
              </button>
            </form>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
